import pageLayoutReducer from './slice/page-layout'
// src/store.js
import { configureStore } from '@reduxjs/toolkit'
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { combineReducers } from '@reduxjs/toolkit'
import sidemenuReducer, { SidemenuItem, updateMenuItems } from './slice/sidemenu'
import appstateReducer, { AppState, updateAppState, updateEnvironment, updateProject, updatePageState } from './slice/appstate'
import notificationReducer, { NotificationType, NotificationsState, addNotification, removeNotification } from './slice/notificationsSlice'
import { AuthReducer, setUser } from '../auth'
import licenseReducer, { updateLicenseState, LicenseState } from './slice/license'
import inboxReducer from './slice/inboxSlice'
import { useSelector } from 'react-redux'


const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'appstate'] 
}

const rootReducers = combineReducers({
  pageLayout: pageLayoutReducer,
  sidemenu: sidemenuReducer,
  auth: AuthReducer,
  appstate: appstateReducer,
  notifications: notificationReducer,
  license: licenseReducer,
  inbox: inboxReducer,
})

const persistedReducer = persistReducer(persistConfig, rootReducers)

const ArtStore = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),

})

export const persistor = persistStore(ArtStore)


function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Attach a showSnack method to the store.
// You can call it as: ArtStore.showSnack("Message", "success")
(ArtStore as any).showSnack = (message: unknown, type: NotificationType = 'info') => {
  const normalizedMessage = typeof message === 'string' ? message.trim() : '';
  if (!normalizedMessage) return;

  const id = uuidv4();
  ArtStore.dispatch(addNotification({ id, message: normalizedMessage, type }));
  setTimeout(() => {
    ArtStore.dispatch(removeNotification(id));
  }, 5000);
};

export {
  // sidemenu slice
  updateMenuItems,

  // app state slice
  updateAppState, updateEnvironment, updateProject, updatePageState,

  // notification state slice
  addNotification, removeNotification,

  //auth slice
  setUser,

  // license slice
  updateLicenseState,


};

export type {
  // sidemenu slice
  SidemenuItem,

  // app state slice
  AppState,

  // notification state slice
  NotificationsState,

  // license slice
  LicenseState,
}
export type RootState = ReturnType<typeof ArtStore.getState>;
export type AppDispatch = typeof ArtStore.dispatch;

export interface AppDefaultsState {
  agent_creation_agent_id: string;
  orchestrator_creation_agent_id: string;
  mission_creation_agent_id: string;
  /** Agent slug, optionally `slug@build`, used by the in-app guide. */
  coach_mark_agent_id?: string;
  source: 'tenant' | 'default' | 'missing';
}

export const useAppDefaults = () => {
  const defaults = useSelector(
    (state: RootState) => (state.auth.user?.app_defaults as AppDefaultsState | undefined) ?? null,
  );
  return {
    defaults,
    loading: false,
    error: defaults && defaults.source !== 'missing'
      ? ''
      : 'Application defaults are not configured for this organisation.',
  };
};

export default ArtStore;
